import React, { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Cancel as CancelIcon, Clear, Done, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';

import DebugModal from '~/components/DebugModal/DebugModal';

import useQuery from '~/hooks/useQuery';

import { CreatePromoTagPayload, PromoRole, PromoTag, buildTagId } from '~/services/PromoService';

import Spinner from '../../Common/Spinner';
import PromoRoleSelector from '../PromoRoles/PromoRoleSelector';
import usePromoAdminPermission from '../hooks/usePromoPermission';
import { usePromoRoles } from '../hooks/usePromoRoles';
import usePromoTagManagement from '../hooks/usePromoTagManagement';

import PromoTagAddForm from './PromoTagAddForm';

type PromoTagRow = PromoTag & {
  isNew?: boolean;
};

function PromoTags() {
  const { enqueueSnackbar } = useSnackbar();
  const { promoTags, isLoading, isLoadingCreate, isLoadingUpdate, error, fetchPromoTags, createTag, updateTag } =
    usePromoTagManagement();
  const { promoRoles, fetchPromoRoles } = usePromoRoles({
    initialFilters: {
      extendedResponse: false,
      includeInactive: false,
      limit: 9999,
      page: 1,
    },
  });
  const query = useQuery();

  const isPromoAdmin = usePromoAdminPermission();

  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [renderExtended, setRenderExtended] = useState<boolean>(query.get('renderExtended') === 'true');
  const [rows, setRows] = useState<Array<PromoTagRow>>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [selectedTag, setSelectedTag] = useState<PromoTag | null>(null);
  const formRef = useRef<{ reset: () => void } | null>(null);

  useEffect(() => {
    fetchPromoTags({
      include_inactive_tags: includeInactive,
      for_member: null,
    });
    fetchPromoRoles();
  }, [includeInactive]);

  useEffect(() => {
    if (promoTags) {
      setRows(promoTags.map((tag) => ({ ...tag, isNew: false })));
    }
  }, [promoTags]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => buildTagId(row) === id);
    if (editedRow?.isNew) {
      setRows((prevRows) => (Array.isArray(prevRows) ? prevRows.filter((row) => buildTagId(row) !== id) : []));
    }
  };

  const processRowUpdate = async (newRow: GridRowModel): Promise<GridRowModel> => {
    const updatedRow = { ...newRow } as PromoTagRow;

    if (updatedRow.isNew) {
      const payload: CreatePromoTagPayload = {
        departmentTag: updatedRow.departmentTag,
        categoryTag: updatedRow.categoryTag,
        subCategoryTag: updatedRow.subCategoryTag,
        isActive: updatedRow.isActive ?? true,
        roleIdRequired: updatedRow.roleIdRequired,
      };

      const createdTag = await createTag(payload);
      if (createdTag) {
        setRows((prevRows) =>
          Array.isArray(prevRows) ? prevRows.filter((row) => buildTagId(row) !== buildTagId(updatedRow)) : [],
        );
        return { ...createdTag, isNew: false };
      }
    } else {
      const updatedTag = await updateTag({
        departmentTag: updatedRow.departmentTag,
        categoryTag: updatedRow.categoryTag,
        subCategoryTag: updatedRow.subCategoryTag,
        isActive: updatedRow.isActive ?? true,
        roleIdRequired: updatedRow.roleIdRequired,
      });

      if (updatedTag) {
        return { ...updatedTag, isNew: false };
      }
    }

    return newRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handlePaginationModelChange = (newPaginationModel: { page: number; pageSize: number }) => {
    setPaginationModel(newPaginationModel);
  };

  const handleAddPromoTag = async (payload: CreatePromoTagPayload) => {
    await createTag(payload);
  };

  const handleRowClick = (params: any) => {
    if (renderExtended) {
      setSelectedTag(params.row);
    }
  };

  const columns = (promoRoles: Array<PromoRole>): Array<GridColDef<PromoTag>> => [
    {
      field: 'departmentTag',
      headerName: 'Department Tag',
      flex: 1,
      editable: false,
    },
    {
      field: 'categoryTag',
      headerName: 'Category Tag',
      flex: 1,
      editable: false,
    },
    {
      field: 'subCategoryTag',
      headerName: 'Sub Category Tag',
      flex: 1,
      editable: false,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      type: 'boolean',
      width: 100,
      editable: true,
      renderEditCell: (params) => {
        if (params.isSystem) {
          return params.row.isActive ? <Done /> : <Clear />;
        }
        return (
          <Checkbox
            checked={params.value || false}
            onChange={(event) =>
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: event.target.checked,
              })
            }
          />
        );
      },
    },
    {
      field: 'roleIdRequired',
      headerName: 'Role Required',
      renderHeader: (params) => {
        return (
          <Tooltip title="When set, this role will be required to create or update a promo code with this tag">
            <Typography>Role Required</Typography>
          </Tooltip>
        );
      },
      type: 'string',
      width: 120,
      editable: true,
      renderCell: (params) => {
        if (!params.row.roleIdRequired) {
          return <Typography>-</Typography>;
        }
        const role = promoRoles.find((r) => r.roleId == params.row.roleIdRequired);
        if (!role) {
          return (
            <Alert severity="warning" title={`no role for id ${role} found (${promoRoles?.length} roles total)`} />
          );
        }

        return <Typography>{role.roleName}</Typography>;
      },
      renderEditCell: (params) => (
        <PromoRoleSelector
          id="roleIdRequired"
          promoRoles={promoRoles}
          selectedRoleId={params.row.roleIdRequired}
          onChange={(event) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: event,
            })
          }
        />
      ),
    },
    {
      field: 'isSystem',
      headerName: 'System',
      type: 'boolean',
      width: 100,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 3,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-save`}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-cancel`}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: 'debug',
      headerName: 'Debug',
      renderCell: ({ row }) => <DebugModal type="generic" data={{ ...row }} />,
    },
  ];

  return (
    <Stack direction="column" spacing={2} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Promo Tags</Typography>
        <Typography variant="caption" color="textSecondary">
          Promo tags are used to categorize promo codes. Enable "Advance" mode to click rows and populate the form
          below, or click the edit icon to modify existing tags
        </Typography>
      </Box>

      <Stack direction="row">
        <FormControlLabel
          control={
            <Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} size="small" />
          }
          label="Show Inactive"
        />

        <FormControlLabel
          control={
            <Switch checked={renderExtended} onChange={(e) => setRenderExtended(e.target.checked)} size="small" />
          }
          label="Advance"
        />
      </Stack>

      {error && (
        <Typography color="error" variant="body2">
          Error: {error}
        </Typography>
      )}

      {isLoadingCreate && <Spinner />}

      {isLoadingUpdate && <Spinner />}

      {isLoading && <Spinner />}
      {!isLoading && (
        <Box>
          <DataGrid
            sx={{
              '& .selected-row': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: renderExtended ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: renderExtended ? 'action.hover' : 'transparent',
                },
              },
            }}
            rows={Array.isArray(rows) ? rows : []}
            columns={columns(promoRoles)}
            editMode="row"
            rowModesModel={rowModesModel}
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            onRowClick={handleRowClick}
            processRowUpdate={processRowUpdate}
            columnVisibilityModel={
              !isPromoAdmin
                ? {
                    actions: false,
                    debug: renderExtended,
                  }
                : {
                    debug: renderExtended,
                  }
            }
            loading={isLoading}
            getRowId={(row: PromoTag) => buildTagId(row)}
            getRowClassName={(params) => {
              return selectedTag && buildTagId(selectedTag) === params.id ? 'selected-row' : '';
            }}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            density="compact"
          />
        </Box>
      )}
      {isPromoAdmin && renderExtended && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {selectedTag && (
            <Box
              sx={{
                mb: 2,
                p: 1,
                bgcolor: 'primary.light',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="primary.contrastText">
                Promo Form Values Populated: {selectedTag.departmentTag} / {selectedTag.categoryTag} /{' '}
                {selectedTag.subCategoryTag}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedTag(null);
                  if (formRef.current) {
                    formRef.current.reset();
                  }
                  enqueueSnackbar('Form cleared', { variant: 'info', autoHideDuration: 2000 });
                }}
                sx={{ color: 'primary.contrastText', borderColor: 'primary.contrastText' }}
              >
                Clear
              </Button>
            </Box>
          )}
          <PromoTagAddForm
            ref={formRef}
            promoRoles={promoRoles || []}
            onSubmit={handleAddPromoTag}
            isLoading={isLoadingCreate}
            initialValues={
              selectedTag
                ? {
                    dept_tag: selectedTag.departmentTag,
                    category_tag: selectedTag.categoryTag,
                    sub_category_tag: selectedTag.subCategoryTag,
                    isActive: selectedTag.isActive,
                    roleIdRequired: selectedTag.roleIdRequired || '',
                  }
                : undefined
            }
          />
        </Box>
      )}
    </Stack>
  );
}

export default PromoTags;
